import React, { useEffect, useState } from "react";

// material-ui

import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  useTheme,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import dayjs from "dayjs";

import { openSnackbar } from "../../../../../api/snackbar";

// assets

// types
import { SnackbarProps } from "../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableModal from "../../../../../components/ReusableComponent/ReusableModal";
import {
  CommentAction,
  CompanyComment,
} from "../../../../../types/site-params/commentAction";
import useAuth from "../../../../../hooks/useAuth";
import { CompanyProfile } from "../../../../../types/companies-managment/CompanyReport";
import { User } from "../../../../../model/user";

// constant
const getInitialValues = (companyComment: CompanyComment | null) => {
  if (companyComment) {
    return {
      action: companyComment.action ? companyComment.action.toString() : "",
      text: companyComment.text || "",
      date: companyComment.date || "",
      to_user_id: companyComment?.to_user?.user_id || "1",
      from_user_id: companyComment?.from_user?.user_id || "",
      company_id: companyComment?.company?.id
        ? companyComment.company?.id.toString()
        : "",
    };
  } else {
    return {
      action: "",
      text: "",
      date: "",
      to_user_id: "1",
      from_user_id: "",
    };
  }
};
// ==============================|| COMPANY COMMENT ADD / EDIT - FORM ||============================== //

const FormCompanyCommentAdd = ({
  open,
  companyComment,
  id,
  loading,
  fetchData,
  setLoading,
  handleClose,
  setResult,
  companyId,
}: {
  open: boolean;
  loading: boolean;
  id: string;
  setLoading: any;
  fetchData: CompanyComment[];
  companyComment: CompanyComment | null;
  handleClose: () => void;
  setResult: any;
  companyId?: string;
}) => {
  const intl = useIntl();
  const [commentActions, setCommentActions] = useState<CommentAction[]>([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const theme = useTheme();
  const { user } = useAuth();
  const [companies, setCompanies] = useState<CompanyProfile[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyProfile | null>(
    null
  );
  const [selectedCommentAction, setSelectedCommentAction] =
    useState<CommentAction | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedFromUser, setSelectedFromUser] = useState<User | null>(null);

  useEffect(() => {
    if (!companyId) fetchCompanies();
  }, [companyId]);

  useEffect(() => {
    if (companyComment && companies.length > 0 && commentActions.length > 0) {
      const selectedCompany = companies.find(
        (company) => company.id === Number(companyComment.company.id)
      );
      if (selectedCompany) setSelectedCompany(selectedCompany);

      const selectedAction = commentActions.find(
        (action) => action.id == companyComment.action.id
      );
      if (selectedAction) setSelectedCommentAction(selectedAction);
    }
  }, [companyComment]);

  useEffect(() => {
    if (companyComment && users.length > 0) {
      const foundUser = users.find(
        (u) => u.id === Number(companyComment.to_user?.user_id)
      );
      setSelectedUser(foundUser || null);

      const foundFromUser = users.find(
        (u) => u.id === Number(companyComment.from_user?.user_id)
      );
      setSelectedFromUser(foundFromUser || null);
    }
  }, [companyComment, users]);

  const fetchCommentActions = async () => {
    setLoadingActions(true);
    try {
      const response = await USERAXIOS.get("/comment_actions?page=1");
      setCommentActions(response.data.data);
    } catch (error) {
      console.error("Error fetching comment actions:", error);
      openSnackbar({
        open: true,
        message: "Error fetching comment actions",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: { color: "error" },
      } as SnackbarProps);
    } finally {
      setLoadingActions(false);
    }
  };

  const CommentActionSchema = Yup.object().shape({
    action: Yup.mixed().required("Comment action is required"),
    text: Yup.string().required("Text is required"),
    date: Yup.string().required("Date is required"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(companyComment!),
    validationSchema: CommentActionSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const commentActionData = {
          action: Number(selectedCommentAction?.id),
          company_id: Number(companyId || selectedCompany?.id),
          text: values.text,
          date: values.date,
          to_user_id: Number(values.to_user_id),
          from_user_id: Number(values.from_user_id),
        };

        if (companyComment) {
          const response = await USERAXIOS.post(
            `/company_comments/${companyComment?.id}`,
            commentActionData
          );
          const updatedCommentAction = response.data.data;

          if (updatedCommentAction) {
            setResult((prev) =>
              prev.map((cat) =>
                cat.id === updatedCommentAction.id ? updatedCommentAction : cat
              )
            );

            openSnackbar({
              open: true,
              message: "Company Comment Edited Successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post(
            `/company_comments`,
            commentActionData
          );

          const newCommentAction = response.data.data;

          if (newCommentAction) {
            setResult((prev) => [...prev, newCommentAction]);

            openSnackbar({
              open: true,
              message: "Company Comment Added Successfully!",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmitting(false);
      }
    },
  });

  const fetchCompanies = async () => {
    try {
      const response = await USERAXIOS.get(`company_profiles`);
      setCompanies(response.data.data);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching companies",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await USERAXIOS.get(
        "/users/select_list?account_type=GENERAL"
      );
      if (response.data.data) {
        setUsers(response.data.data);
      }
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching users",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: { color: "error" },
      } as SnackbarProps);
    } finally {
      setUsersLoading(false);
    }
  };
  useEffect(() => {
    fetchCompanies();
    fetchCommentActions();
    fetchUsers();
  }, []);

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  // if (loading)
  //   return (
  //     <Box sx={{ p: 5 }}>
  //       <Stack direction="row" justifyContent="center">
  //         <CircularWithPath />
  //       </Stack>
  //     </Box>
  //   );

  return (
    <ReusableModal open={open} handleClose={handleClose}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <DialogTitle
            color="primary"
            style={{
              textAlign: "center",
              fontSize: "22px",
              fontWeight: "bold",
              marginBottom: "10px",
            }}
          >
            {companyComment ? (
              <FormattedMessage id="Edit Company Comment" />
            ) : (
              <FormattedMessage id="New Company Comment" />
            )}
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="comment-action-select">
                    <FormattedMessage id="Comment Action" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <Autocomplete
                    options={commentActions}
                    getOptionLabel={(option) =>
                      theme.direction === "rtl"
                        ? option?.value_trans?.ar
                        : option?.value
                    }
                    value={selectedCommentAction}
                    onChange={(_, newValue) => {
                      setSelectedCommentAction(newValue);
                      setFieldValue("action", newValue);
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<FormattedMessage id="Comment Action" />}
                        error={Boolean(touched.action && errors.action)}
                        helperText={touched.action && errors.action}
                        variant="outlined"
                      />
                    )}
                    disabled={loadingActions}
                  />
                </Stack>
              </Grid>

              {!companyId && (
                <>
                  <Grid item xs={12} md={6} mb={1}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="company-profile-select">
                        <FormattedMessage id="Company profiles" />{" "}
                        <span style={{ color: "red" }}>*</span>
                      </InputLabel>
                      <Autocomplete
                        options={companies}
                        onChange={(_, newValue) => {
                          setSelectedCompany(newValue);
                          setFieldValue("company_id", newValue);
                        }}
                        getOptionLabel={(option) =>
                          theme.direction === "rtl"
                            ? option?.company_name_translations?.ar
                            : option?.company_name
                        }
                        value={selectedCompany}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Company"
                            variant="outlined"
                          />
                        )}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack>
                      <InputLabel
                        htmlFor="from-user-autocomplete"
                        style={{ marginBottom: "10px" }}
                      >
                        <FormattedMessage id="From User" />
                      </InputLabel>
                      <FormControl fullWidth>
                        <InputLabel id="from-user-select-label">
                          Select from user
                        </InputLabel>
                        <Select
                          labelId="from-user-select-label"
                          id="from-user-select"
                          value={selectedFromUser ? selectedFromUser.id : ""}
                          label="Select from user"
                          onChange={(e) => {
                            const user = users.find(
                              (u) => u.id === e.target.value
                            );
                            setSelectedFromUser(user || null);
                            setFieldValue("from_user_id", user ? user.id : "");
                          }}
                          renderValue={(selected) => {
                            const user = users.find((u) => u.id === selected);
                            return user
                              ? user.user_name || user.email || String(user.id)
                              : "";
                          }}
                        >
                          {users.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.user_name ||
                                option.email ||
                                String(option.id)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={6}>
                <Stack>
                  <InputLabel
                    htmlFor="user-autocomplete"
                    style={{ marginBottom: "10px" }}
                  >
                    <FormattedMessage id="To User" />
                  </InputLabel>
                  <FormControl fullWidth>
                    <InputLabel id="user-select-label">
                      Select to user
                    </InputLabel>
                    <Select
                      labelId="user-select-label"
                      id="user-select"
                      value={selectedUser ? selectedUser.id : ""}
                      label="Select to user"
                      onChange={(e) => {
                        const user = users.find((u) => u.id === e.target.value);
                        setSelectedUser(user || null);
                        setFieldValue("to_user_id", user ? user.id : "");
                      }}
                      renderValue={(selected) => {
                        const user = users.find((u) => u.id === selected);
                        return user
                          ? user.user_name || user.email || String(user.id)
                          : "";
                      }}
                    >
                      {users.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.user_name ||
                            option.email ||
                            String(option.id)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Grid>
              <Grid item xs={12} md={12} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="company-comment-text">
                    <FormattedMessage id="Text" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="company-comment-text"
                    multiline
                    rows={4}
                    placeholder={intl.formatMessage({
                      id: "Enter comment text",
                      defaultMessage: "Enter comment text",
                    })}
                    {...getFieldProps("text")}
                    error={Boolean(touched.text && errors.text)}
                    helperText={touched.text && errors.text}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} mb={1}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="company-comment-date">
                    <FormattedMessage id="Date" />{" "}
                    <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={
                        formik.values.date ? dayjs(formik.values.date) : null
                      }
                      onChange={(date) => {
                        setFieldValue(
                          "date",
                          date ? date.format("YYYY-MM-DD") : null
                        );
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: Boolean(touched.date && errors.date),
                          helperText: touched.date && errors.date,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Grid container justifyContent="center" alignItems="center">
              <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                <Grid item md={2} />
                <Grid item md={4}>
                  {companyComment ? (
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage id="Update" />
                    </Button>
                  ) : (
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage id="Submit" />
                    </Button>
                  )}
                </Grid>
                <Grid item md={4}>
                  <Button
                    fullWidth
                    color="secondary"
                    variant="outlined"
                    onClick={handleClose}
                  >
                    <FormattedMessage id="Cancel" />
                  </Button>
                </Grid>
                <Grid item md={2} />
              </Stack>
            </Grid>
          </DialogActions>
        </Form>
      </FormikProvider>
    </ReusableModal>
  );
};

export default FormCompanyCommentAdd;
